import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeartIcon, ShieldCheckIcon, StarIcon, ChatBubbleLeftRightIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

const FEATURES = [
  { icon: HeartIcon,               title: 'Verified Profiles', desc: 'Every profile verified for authenticity. Your safety is our priority.' },
  { icon: ShieldCheckIcon,         title: 'Privacy Protected', desc: 'Your personal details shown only to matched members.' },
  { icon: StarIcon,                title: 'Smart Matching',    desc: 'AI-powered compatibility matching based on denomination, education and values.' },
  { icon: ChatBubbleLeftRightIcon, title: 'Instant Chat',      desc: 'Chat securely once both profiles accept interest.' },
];
const TESTIMONIALS = [
  { name: 'Priya & David',  location: 'Vijayawada', text: 'Found my life partner in just 3 months. Praise God!' },
  { name: 'Sneha & Samuel', location: 'Hyderabad',  text: 'This platform truly understands Christian values. Highly recommended!' },
  { name: 'Ananya & John',  location: 'Guntur',     text: 'Beautiful experience. Met my soulmate through this wonderful app.' },
];
const STEPS = [
  { n: '01', title: 'Create your profile', desc: 'Free, in under 3 minutes. Tell us about yourself and your faith community.' },
  { n: '02', title: 'Get matched', desc: 'We show you profiles of the opposite gender that fit your preferences automatically.' },
  { n: '03', title: 'Connect & chat', desc: 'Express interest, get accepted, and chat securely — all in one place.' },
];

const reveal = { hidden: { opacity: 0, y: 28 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.2, 0.7, 0.2, 1] } } };
const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div className={className} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.25 }} transition={{ delay }}>{children}</motion.div>
);

export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 overflow-x-hidden">
      {/* ── Header ── */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? 'bg-cream-50/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/cross-icon.png" alt="Holyweds" className="w-8 h-8 md:w-9 md:h-9 object-contain" />
            <span className="text-primary font-brand italic font-bold text-xl md:text-2xl tracking-tight">Holy<span className="text-gold-600">weds</span></span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="glass-light hover:bg-primary/10 text-primary text-sm font-semibold px-4 py-2 rounded-lg transition-all">Sign In</Link>
            <Link to="/register" className="btn-gold text-sm font-bold py-2 px-5 shadow-lg">Register Free</Link>
          </div>
        </div>
      </header>

      {/* ── Hero: split layout with real photo ── */}
      <section className="aurora stars relative overflow-hidden text-primary pt-32 pb-24 px-5">
        <span className="floaty text-2xl" style={{ left: '6%', top: '22%', animationDelay: '1.5s' }}>✨</span>
        <span className="floaty text-2xl" style={{ left: '10%', bottom: '14%', animationDelay: '2.4s' }}>💍</span>

        <div className="max-w-7xl mx-auto relative z-10 grid lg:grid-cols-2 gap-14 items-center">
          {/* Left: copy */}
          <div className="text-center lg:text-left">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 glass-light px-4 py-2 rounded-full text-sm mb-6">
              <img src="/cross-icon.png" alt="Cross" className="w-4 h-4 object-contain" />
              <span className="font-subtitle tracking-wider uppercase text-xs text-primary">Exclusively for the Telugu Christian Community</span>
            </motion.div>

            <motion.img initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
              src="/logo-full.png" alt="Holyweds — Christian Matrimony" className="w-40 md:w-48 mx-auto lg:mx-0 mb-4" />

            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl md:text-6xl font-bold leading-tight text-primary">
              Find Your God-Given<br /><span className="shimmer-gold">Life Partner</span>
            </motion.h1>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35, duration: 0.7 }}
              className="text-primary-500 text-lg mt-5 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
              The most trusted Christian matrimony platform for Andhra Pradesh &amp; Telangana. Join families who found their perfect match through faith.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4">
              <Link to="/register" className="btn-gold text-lg px-8 py-3 w-full sm:w-auto text-center shadow-xl">Register for Free</Link>
              <Link to="/login" className="glass-light hover:bg-primary/10 text-primary font-semibold text-lg px-8 py-3 rounded-lg transition-all w-full sm:w-auto text-center">Sign In</Link>
            </motion.div>
            <p className="text-primary-400 text-sm mt-5">✨ Free registration. No credit card required.</p>
          </div>

          {/* Right: featured photo */}
          <Reveal delay={0.2} className="relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              <div className="absolute -inset-3 rounded-[2rem] bg-gold-gradient opacity-40 blur-2xl" aria-hidden="true" />
              <div className="relative rounded-[1.75rem] overflow-hidden border-2 border-gold-200/60 shadow-2xl">
                <div className="kenburns">
                  <img src="/hero-couple.jpg" alt="A Telugu Christian couple blessed in marriage outside a church" className="w-full h-[420px] object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/60 via-transparent to-transparent" />
              </div>
              <div className="absolute -bottom-6 -left-4 md:-left-8 glass rounded-2xl px-5 py-3 flex items-center gap-3 shadow-xl">
                <CheckBadgeIcon className="w-8 h-8 text-gold-300" />
                <div className="text-left">
                  <p className="text-white font-semibold text-sm leading-tight">Verified & Blessed</p>
                  <p className="text-primary-100 text-xs">Real families, real matches</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="absolute -bottom-px inset-x-0 h-12 bg-cream-100" style={{ clipPath: 'ellipse(75% 100% at 50% 100%)' }} />
      </section>

      {/* ── How it works ── */}
      <section className="py-20 px-5 bg-cream-50">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-gold-600 font-subtitle font-semibold tracking-wide uppercase text-xs mb-2">Simple &amp; Free</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">Three Steps to Forever</h2>
            <p className="text-gray-500 max-w-xl mx-auto">No confusing sections, no clutter — just a clear path to your match.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {STEPS.map(({ n, title, desc }, i) => (
              <Reveal key={n} delay={i * 0.1}>
                <div className="card lift p-7 h-full relative overflow-hidden">
                  <span className="absolute -top-2 -right-1 font-heading font-bold text-6xl text-primary-50 select-none">{n}</span>
                  <h3 className="font-heading font-semibold text-gray-900 mb-2 text-lg relative">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed relative">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 px-5 bg-gradient-to-b from-cream-200 to-cream-50">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-gold-600 font-subtitle font-semibold tracking-widest uppercase text-xs mb-2">Why Holyweds</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3">Built on Faith &amp; Trust</h2>
            <p className="text-gray-500 max-w-xl mx-auto">Designed specifically for the Telugu Christian community, with your values at the core.</p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <Reveal key={title} delay={i * 0.08}>
                <div className="card lift p-7 text-center h-full border-t-4 border-t-gold">
                  <div className="w-14 h-14 bg-gradient-to-br from-primary-50 to-gold-100 rounded-2xl flex items-center justify-center mx-auto mb-5"><Icon className="w-7 h-7 text-primary" /></div>
                  <h3 className="font-heading font-semibold text-gray-900 mb-2 text-lg">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="py-20 px-5 bg-cream-50">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-3 flex items-center justify-center gap-2">Blessed Unions <img src="/cross-icon.png" alt="Cross" className="w-7 h-7 object-contain inline-block" /></h2>
            <p className="text-gray-500">Real stories from our happy families.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(({ name, location, text }, i) => (
              <Reveal key={name} delay={i * 0.1}>
                <div className="card lift p-7 h-full">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-gold-100 rounded-full flex items-center justify-center mx-auto mb-4 text-3xl">💒</div>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 italic text-center">“{text}”</p>
                  <p className="font-semibold text-gray-900 text-center">{name}</p>
                  <p className="text-gold-600 text-xs text-center mt-0.5">{location}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="aurora stars relative overflow-hidden py-20 px-5 text-center">
        <Reveal className="max-w-2xl mx-auto relative z-10">
          <div className="text-4xl mb-4">💍</div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary mb-4">Your Story Begins Here</h2>
          <p className="text-primary-500 mb-8">Join the largest Telugu Christian matrimony platform and find your God-chosen partner.</p>
          <Link to="/register" className="btn-gold text-lg px-10 py-3 inline-block shadow-xl">Start for Free Today</Link>
        </Reveal>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-cream-200 text-primary-600 py-10 px-5 text-center text-sm border-t border-cream-300">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img src="/cross-icon.png" alt="Cross" className="w-6 h-6 object-contain" />
          <span className="text-primary font-brand italic font-bold text-xl">Holy<span className="text-gold-600">weds</span></span>
        </div>
        <p className="mb-2">Exclusively for the Telugu Christian Community</p>
        <p>Andhra Pradesh &amp; Telangana | <a href="mailto:contact@holyweds.com" className="hover:text-primary-900 underline-offset-2 hover:underline">contact@holyweds.com</a> | <a href="tel:9494623015" className="hover:text-primary-900 underline-offset-2 hover:underline">+91 94946 23015</a></p>
        <p className="mt-3 text-xs flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
          <a href="/contact/" className="hover:text-primary-900 underline-offset-2 hover:underline">Contact Us</a>
          <span className="text-primary-300">·</span>
          <a href="/privacy-policy/" className="hover:text-primary-900 underline-offset-2 hover:underline">Privacy Policy</a>
          <span className="text-primary-300">·</span>
          <a href="/terms-and-conditions/" className="hover:text-primary-900 underline-offset-2 hover:underline">Terms &amp; Conditions</a>
          <span className="text-primary-300">·</span>
          <a href="/refund-policy/" className="hover:text-primary-900 underline-offset-2 hover:underline">Refund &amp; Cancellation Policy</a>
        </p>
        <p className="mt-4 text-xs text-primary-300">© {new Date().getFullYear()} Holyweds. All rights reserved.</p>
      </footer>
    </div>
  );
}
